import React from 'react';
import { Image, Pressable, type StyleProp, type ViewStyle } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import { imgNotiBell } from '@/assets/images';
import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const WARNING_GRADIENT = ['#FFF2D8', '#FFF8E9'];

type NotificationPermissionBannerProps = {
  message: string;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
};

export function NotificationPermissionBanner({
  message,
  onPress,
  style,
  contentStyle,
}: NotificationPermissionBannerProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const disabled = !onPress;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={message}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [style, pressed && !disabled ? styles.pressed : null]}>
      <LinearGradient
        colors={WARNING_GRADIENT}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[styles.gradient, contentStyle]}>
        <Stack direction="row" align="center" gap={scale(7)} style={styles.content}>
          <Image source={imgNotiBell} style={styles.icon} />
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            numberOfLines={2}
            style={styles.text}>
            {message}
          </Text>
          <SvgIcon name="IcPaginationArrow" size={scale(20)} color={guardianColors['6']} />
        </Stack>
      </LinearGradient>
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  gradient: {
    borderRadius: scale(12),
    borderWidth: 0.5,
    borderColor: guardianColors['12'],
  },
  content: {
    padding: scale(8),
  },
  icon: {
    width: scale(32),
    height: scale(32),
  },
  text: {
    flex: 1,
    color: colors['10'],
  },
  pressed: {
    opacity: 0.72,
  },
}));
